import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EmbeddedViewRef,
  EnvironmentInjector,
  Injectable,
  TemplateRef,
  Type,
} from '@angular/core';
import * as bootstrap from 'bootstrap';
import { delay, fromEvent, Observable, take } from 'rxjs';
import { ModalOptions } from '../modal-options';
import { ModalRef } from '../modal-ref';

@Injectable({ providedIn: 'root' })
export class ModalService {
  public modalObjects = [];
  componentRef!: ComponentRef<unknown>;
  focusElement!: HTMLElement;

  constructor(
    private applicationRef: ApplicationRef,
    private environmentInjector: EnvironmentInjector,
  ) {}

  public show<T>(
    content: TemplateRef<unknown> | Type<T>,
    config?: ModalOptions<T>,
  ): ModalRef<T> {
    this.focusElement = document.activeElement as HTMLElement;
    if (content instanceof Type) {
      this.showModalComponent(content, config);
    } else {
      this.showModalTemplate(content, config);
    }

    return this.modalObjects.at(-1) as unknown as ModalRef<T>;
  }

  public hide() {
    (this.modalObjects.at(-1) as unknown as bootstrap.Modal).hide();
  }

  private showModalTemplate<T>(
    content: TemplateRef<unknown>,
    config: ModalOptions<T> = {},
  ) {
    const viewRef = content?.createEmbeddedView({});
    this.applicationRef?.attachView(viewRef);

    const modalContainer = this.addModalContainer(config.class);
    for (const node of viewRef.rootNodes) {
      modalContainer.querySelector('.modal-content')?.append(node);
    }
    this.setAriaLabelledByIfPresent(modalContainer);
    this.createModalDom(modalContainer, config);

    this.setFocusAfterModalClose();
    this.onHide(this.modalObjects.at(-1) as unknown as ModalRef<T>)
      .pipe(take(1))
      .subscribe(() => {
        this.modalObjects.pop();
      });
    this.onHidden(this.modalObjects.at(-1) as unknown as ModalRef<T>)
      .pipe(take(1))
      .subscribe(() => {
        viewRef.destroy();
      });
  }

  private showModalComponent<T>(
    content: Type<T>,
    config: ModalOptions<T> = {},
  ) {
    //create a component reference
    this.componentRef = createComponent<T>(content, {
      environmentInjector: this.environmentInjector,
    });
    this.applicationRef?.attachView(this.componentRef.hostView);
    // get DOM element from component
    const domElem = (this.componentRef.hostView as EmbeddedViewRef<unknown>)
      .rootNodes[0] as HTMLElement;

    const modalContainer = this.addModalContainer(config.class);
    modalContainer.querySelector('.modal-content')?.append(domElem);

    this.setAriaLabelledByIfPresent(modalContainer);
    this.createModalDom(modalContainer, config, this.componentRef);

    this.setFocusAfterModalClose();
    this.onHide(this.modalObjects.at(-1) as unknown as ModalRef<T>)
      .pipe(take(1))
      .subscribe(() => {
        this.modalObjects.pop();
      });
    this.onHidden(this.modalObjects.at(-1) as unknown as ModalRef<T>)
      .pipe(take(1))
      .subscribe(() => {
        if (this.componentRef) {
          this.componentRef.destroy();
        }
      });
  }

  private createModalDom<T>(
    modalContainer: HTMLElement,
    config: ModalOptions<T>,
    componentRef?: ComponentRef<T>,
  ) {
    const modalObject = new bootstrap.Modal(modalContainer, {
      backdrop: config.backdrop === undefined ? true : config.backdrop,
      keyboard: config.keyboard === undefined ? true : config.keyboard,
      focus: config.focus === undefined ? true : config.focus,
    });

    if (componentRef) {
      (modalObject as unknown as ModalRef<T>).content = componentRef.instance;

      (componentRef.instance as Record<string, unknown>)['modalRef'] =
        modalObject;
      if (config.initialState) {
        for (const [key, value] of Object.entries(config.initialState)) {
          (componentRef.instance as Record<string, unknown>)[key] = value;
        }
      }
    }

    modalObject.show();
    (this.modalObjects as unknown as bootstrap.Modal[]).push(modalObject);

    const existingModals = document.querySelectorAll('.modal');
    if (existingModals.length > 0) {
      // flytta existerande modal bakom backdrop (zIndex 1040)
      ([...existingModals].at(-1) as HTMLElement).style.zIndex = '1039';
    }

    modalContainer.addEventListener('hidden.bs.modal', function hiddenEvent() {
      modalContainer.removeEventListener('hidden.bs.modal', hiddenEvent);
      modalContainer.remove();
      if (existingModals.length > 0) {
        // återställ default zIndex på bakomliggande modal
        ([...existingModals].at(-1) as HTMLElement).style.zIndex = '1050';
      }
    });

    modalContainer.addEventListener('hide.bs.modal', function hideEvent() {
      modalContainer.removeEventListener('hidden.bs.modal', hideEvent);
      const existingModals = document.querySelectorAll('.modal');
      if (existingModals.length > 1) {
        // flytta bakomliggande modal framför backdrop (men fortfarande bakom modalen som håller på att tas bort)
        ([...existingModals].at(-2) as HTMLElement).style.zIndex = '1040';
      }
    });
  }

  public onShown(modalRef: ModalRef): Observable<Event> {
    return fromEvent(modalRef._element, 'shown.bs.modal');
  }

  public onHide(modalRef: ModalRef): Observable<Event> {
    return fromEvent(modalRef._element, 'hide.bs.modal');
  }

  public onHidden(modalRef: ModalRef): Observable<Event> {
    return fromEvent(modalRef._element, 'hidden.bs.modal');
  }

  private addModalContainer(modalClass?: string): HTMLDivElement {
    const modal = document.createElement('div');
    modal.className = 'modal fade';
    modal.tabIndex = -1;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-hidden', 'true');

    const modalDialog = document.createElement('div');
    modalDialog.className = modalClass
      ? `modal-dialog ${modalClass}`
      : 'modal-dialog';

    const modalContent = document.createElement('div');
    modalContent.className = 'modal-content';

    modalDialog.append(modalContent);
    modal.append(modalDialog);

    return modal;
  }

  // Sätt aria-labelledby endast om ett element med id="modalTitle" faktiskt
  // finns i modalen. Mallbaserade modaler kan inkludera detta id; lib-modal
  // sätter sitt eget unika id och behöver ingen fallback här.
  private setAriaLabelledByIfPresent(modalContainer: HTMLElement) {
    if (modalContainer.querySelector('#modalTitle')) {
      modalContainer.setAttribute('aria-labelledby', 'modalTitle');
    }
  }

  private setFocusAfterModalClose() {
    if (this.focusElement) {
      const focusable = document.querySelectorAll(
        'input, textarea, button, a, [tabindex="0"]:not(.focus-anchor)',
      );
      const focusElementIndex = [...focusable].indexOf(this.focusElement);

      this.onHide(this.modalObjects.at(-1) as unknown as ModalRef<unknown>)
        .pipe(take(1), delay(0))
        .subscribe(() => {
          if (this.focusElement.offsetWidth) {
            this.focusElement.focus();
          } else {
            const nextFocusable = this.getNextFocusableElement(
              focusElementIndex,
              focusable.length,
            );
            nextFocusable?.focus();
          }
        });
    }
  }

  private getNextFocusableElement(
    focusElementIndex: number,
    oldFocusableLength: number,
  ) {
    const focusable = document.querySelectorAll(
      'input, textarea, button, a, [tabindex="0"]:not(.focus-anchor)',
    );
    let index = focusElementIndex;
    if (oldFocusableLength - focusable.length > 0) {
      index = focusElementIndex - 1;
    }
    while (focusable.length > index) {
      const element = focusable[index] as HTMLElement;
      if (element?.offsetWidth) {
        return element;
      }
      index++;
    }
  }
}
